import { createFileRoute, notFound } from "@tanstack/react-router";
import LearnArticle from "@/pages/LearnArticle";
import CmsArticlePage from "@/pages/CmsArticlePage";
import { getArticleBySlug } from "@/data/articles";
import { getPublishedCmsArticle, type CmsArticle } from "@/lib/cmsArticles.functions";
import NotFound from "@/pages/NotFound";
import { pageHead, unavailableHead, hasUnavailableMatch } from "@/lib/seoPolicy";

export const Route = createFileRoute("/_public/learn/$slug")({
  loader: async ({ params }) => {
    // Hand-built articles always win — existing URLs never change behaviour.
    if (getArticleBySlug(params.slug)) return { cms: null as CmsArticle | null };
    const cms = await getPublishedCmsArticle({ data: { slug: params.slug } });
    if (!cms) throw notFound();
    return { cms: cms as CmsArticle | null };
  },
  head: ({ params, loaderData, matches }) => {
    if (!loaderData || hasUnavailableMatch(matches)) return unavailableHead();
    const builtIn = getArticleBySlug(params.slug);
    const cms = loaderData.cms;
    const title = builtIn?.seoTitle ?? builtIn?.title ?? (cms?.meta_title || cms?.title);
    if (!title) return unavailableHead();
    const description = builtIn?.description ?? (cms?.meta_description || cms?.excerpt || "");
    const head = pageHead(title, description, `/learn/${params.slug}`, false, "article");
    const image = builtIn?.heroImage ?? cms?.featured_image_url;
    if (image?.startsWith("https://")) head.meta.push(
      { property: "og:image", content: image },
      { name: "twitter:image", content: image },
    );
    return head;
  },
  component: LearnArticleRoute,
  errorComponent: () => <section role="alert" className="container py-20"><h1 className="text-2xl font-bold">Article temporarily unavailable</h1><p>Please try again shortly.</p></section>,
  notFoundComponent: NotFound,
});

function LearnArticleRoute() {
  const { cms } = Route.useLoaderData();
  if (cms) return <CmsArticlePage article={cms} />;
  return <LearnArticle />;
}
